import _extends from "@babel/runtime/helpers/extends";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

/** @jsx jsx */
import { useCallback, useEffect, useState } from 'react';
import { css, jsx } from '@emotion/react';
import { useGlobalTheme } from '@atlaskit/theme/components';
import { borderRadius, //gridSize as getGridSize,
layers } from '@atlaskit/theme/constants';
import { usePlatformLeafEventHandler } from '@atlaskit/analytics-next/usePlatformLeafEventHandler';
import noop from '@atlaskit/ds-lib/noop';
import FocusRing from '@atlaskit/focus-ring';
import { DEFAULT_APPEARANCE } from './constants';
import { flagBorderColor, flagShadowColor, getFlagBackgroundColor, getFlagTextColor, getFlagIconColor } from './theme';
import Actions from './flag-actions';
import { useFlagGroup } from './flag-group';
import { Title, Description, Expander, DismissButton } from './internal';
var analyticsAttributes = {
  componentName: 'flag',
  packageName: "@atlaskit/flag",
  packageVersion: "14.7.3"
}; // NOT USED
// const gridSize = getGridSize();
// const doubleGridSize = gridSize * 2;

var titleGroupStyles = css({
  display: 'flex',
  alignItems: 'start'
});
var iconTitleStyles = css({
  display: 'flex',
  // TODO Delete this comment after verifying spacing token -> previous value ``${gridSize / 2}px``
  paddingTop: "var(--ds-scale-050, 4px)",
  alignItems: 'start',
  flex: 1
});
var flagHeaderStyles = css({
  boxSizing: 'border-box',
  width: '100%',
  // TODO Delete this comment after verifying spacing token -> previous value `doubleGridSize`
  padding: "var(--ds-scale-200, 16px)",
  borderRadius: borderRadius()
});
var flagContainerStyles = css({
  width: '100%',
  zIndex: layers.flag(),
  borderRadius: borderRadius(),
  transition: 'background-color 200ms'
});
/**
 * __Flag__
 *
 * A flag is used for confirmations, alerts, and acknowledgments that require minimal user interaction,
 * often displayed using a flag group.
 *
 * - [Examples](https://atlassian.design/components/flag/examples)
 * - [Code](https://atlassian.design/components/flag/code)
 * - [Usage](https://atlassian.design/components/flag/usage)
 */

var Flag = function Flag(props) {
  var _props$actions = props.actions,
      actions = _props$actions === void 0 ? [] : _props$actions,
      _props$appearance = props.appearance,
      appearance = _props$appearance === void 0 ? DEFAULT_APPEARANCE : _props$appearance,
      icon = props.icon,
      title = props.title,
      description = props.description,
      linkComponent = props.linkComponent,
      onMouseOver = props.onMouseOver,
      _props$onFocus = props.onFocus,
      onFocus = _props$onFocus === void 0 ? noop : _props$onFocus,
      onMouseOut = props.onMouseOut,
      _props$onBlur = props.onBlur,
      onBlur = _props$onBlur === void 0 ? noop : _props$onBlur,
      _props$onDismissed = props.onDismissed,
      onDismissedProp = _props$onDismissed === void 0 ? noop : _props$onDismissed,
      testId = props.testId,
      id = props.id,
      analyticsContext = props.analyticsContext;

  var _useFlagGroup = useFlagGroup(),
      onDismissedFromFlagGroup = _useFlagGroup.onDismissed,
      isDismissAllowed = _useFlagGroup.isDismissAllowed;

  var onDismissed = useCallback(function (id, analyticsEvent) {
    onDismissedProp(id, analyticsEvent);
    onDismissedFromFlagGroup(id, analyticsEvent);
  }, [onDismissedProp, onDismissedFromFlagGroup]);

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      isExpanded = _useState2[0],
      setIsExpanded = _useState2[1];

  var onDismissedAnalytics = usePlatformLeafEventHandler(_objectSpread({
    fn: onDismissed,
    action: 'dismissed',
    analyticsData: analyticsContext
  }, analyticsAttributes));
  var isBold = appearance !== DEFAULT_APPEARANCE;
  var toggleExpand = useCallback(function () {
    setIsExpanded(function (previous) {
      return !previous;
    });
  }, []);
  var buttonActionCallback = useCallback(function () {
    if (isDismissAllowed) {
      onDismissedAnalytics(id);
    }
  }, [onDismissedAnalytics, id, isDismissAllowed]);
  useEffect(function () {
    // If buttons are removed as a prop, update isExpanded to be false
    if (isBold && isExpanded && !description && !actions.length) {
      setIsExpanded(false);
    }
  }, [actions.length, description, isBold, isExpanded]);
  var onFocusAnalytics = usePlatformLeafEventHandler(_objectSpread({
    fn: onFocus,
    action: 'focused',
    analyticsData: analyticsContext
  }, analyticsAttributes));
  var onBlurAnalytics = usePlatformLeafEventHandler(_objectSpread({
    fn: onBlur,
    action: 'blurred',
    analyticsData: analyticsContext
  }, analyticsAttributes));
  var autoDismissProps = {
    onMouseOver: onMouseOver,
    onFocus: onFocusAnalytics,
    onMouseOut: onMouseOut,
    onBlur: onBlurAnalytics
  };
  var boxShadowValue = "0 20px 32px -8px ".concat(flagShadowColor);

  if (!isBold) {
    boxShadowValue = "0 0 1px ".concat(flagBorderColor, ", ").concat(boxShadowValue);
  }

  var boxShadow = "var(--ds-shadow-overlay, ".concat(boxShadowValue, ")");

  var _useGlobalTheme = useGlobalTheme(),
      mode = _useGlobalTheme.mode;

  var textColor = getFlagTextColor(appearance, mode);
  var iconColor = getFlagIconColor(appearance, mode);
  var isDismissable = isBold || isDismissAllowed;
  return jsx("div", _extends({
    style: {
      color: textColor,
      backgroundColor: getFlagBackgroundColor(appearance, mode),
      boxShadow: boxShadow
    },
    css: flagContainerStyles,
    role: "alert",
    "data-testid": testId
  }, autoDismissProps), jsx(FocusRing, null, jsx("div", {
    css: flagHeaderStyles // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
    ,
    tabIndex: 0
  }, jsx("div", {
    style: {
      color: iconColor
    },
    css: titleGroupStyles
  }, jsx("div", {
    css: iconTitleStyles
  }, icon, jsx(Title, {
    color: textColor
  }, title)), isDismissable ? !(isBold && !description && !actions.length) && jsx(DismissButton, {
    testId: testId,
    appearance: appearance,
    isBold: isBold,
    isExpanded: isExpanded,
    onClick: isBold ? toggleExpand : buttonActionCallback
  }) : null), jsx(Expander, {
    isExpanded: !isBold || isExpanded,
    testId: testId
  }, description && jsx(Description, {
    testId: testId && "".concat(testId, "-description"),
    color: textColor,
    isBold: isBold
  }, description), jsx(Actions, {
    actions: actions,
    appearance: appearance,
    linkComponent: linkComponent,
    testId: testId,
    mode: mode
  })))));
};

export default Flag;